import { useState, useEffect, createContext, useContext } from 'react';
import { apiService } from '../services/api';
import { User, Token } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loginWithTelegram: (payload: {
    id: number;
    auth_date: number;
    hash: string;
    first_name?: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
  }) => Promise<void>;
  loginWithTelegramWebApp: (initData: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('access_token');

      if (storedToken) {
        try {
          setToken(storedToken);
          // Берём актуального пользователя с сервера (в т.ч. для is_superuser=0/3 вернётся 403)
          const userData: User = await apiService.getCurrentUser();
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
        } catch (error) {
          // Токен недействителен, очищаем
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Удалено: классический логин не используется

  const loginWithTelegram = async (payload: {
    id: number;
    auth_date: number;
    hash: string;
    first_name?: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
  }) => {
    const response: Token = await apiService.telegramVerify(payload);
    localStorage.setItem('access_token', response.access_token);
    setToken(response.access_token);
    const userData: User = await apiService.getCurrentUser();
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const loginWithTelegramWebApp = async (initData: string) => {
    const response: Token = await apiService.telegramWebAppVerify(initData);
    localStorage.setItem('access_token', response.access_token);
    setToken(response.access_token);
    const userData: User = await apiService.getCurrentUser();
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  // Удалено: регистрация не используется

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loginWithTelegram,
    loginWithTelegramWebApp,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 